import { Link } from "@tanstack/react-router";
import { useId, type ReactNode } from "react";
import { cx } from "../../ui";
import { Icon, type IconName } from "../../ui/icons";
import { safeHref } from "../chat/markdown";
import { fromDateInput, SOURCE_LABEL, toDateInput, type Task } from "./model";
import type { TaskPatch } from "./use-autosave";
import { memberLabel, useMembers } from "./use-task-actions";

/** Controls that look like text until hovered or focused, as in the mockup's property list. */
const quiet =
  "min-h-8 max-w-full rounded-md border-0 bg-transparent px-2 text-sm text-text hover:bg-surface-2 focus:bg-surface-2 disabled:cursor-default disabled:hover:bg-transparent";

const STATUS_TEXT: Record<Task["status"], string> = { open: "Open", done: "Done", cancelled: "Cancelled" };

function Row({ icon, label, htmlFor, children }: { icon: IconName; label: string; htmlFor?: string; children: ReactNode }) {
  const name = (
    <span className="flex items-center gap-2 text-sm text-muted">
      <Icon name={icon} className="size-4" />
      {label}
    </span>
  );
  return (
    <div className="grid grid-cols-[7.5rem_minmax(0,1fr)] items-center gap-2 sm:grid-cols-[9rem_minmax(0,1fr)]">
      {htmlFor === undefined ? <dt>{name}</dt> : <dt><label htmlFor={htmlFor}>{name}</label></dt>}
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}

function webLink(ref: string): { href: string; host: string } | null {
  const href = safeHref(ref);
  if (href === null) return null;
  const url = new URL(href);
  return url.protocol === "mailto:" ? null : { href, host: url.hostname };
}

function shortDate(d: Date): string {
  return d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
}

/** Status, assignee, due date, visibility and source; each change saves at once. */
export function TaskProperties({
  workspaceId,
  task,
  personal,
  myUid,
  onSave,
}: {
  workspaceId: string;
  task: Task;
  personal: boolean;
  myUid: string;
  onSave: (patch: TaskPatch) => void;
}) {
  const members = useMembers(workspaceId);
  const ids = { assignee: useId(), due: useId(), visibility: useId() };
  const isCreator = task.creatorUid === myUid;
  // Only a web link opens: an email's ref is a message id, a chat's a channel.
  const href = task.source === null ? null : webLink(task.source.ref);

  return (
    <dl className="flex flex-col gap-1">
      <Row icon="status" label="Status">
        <span className={cx("px-2 text-sm", task.status === "open" ? "text-text" : "text-muted")}>
          {STATUS_TEXT[task.status]}
          {task.status === "done" && task.doneAt !== null ? ` · ${shortDate(task.doneAt)}` : null}
        </span>
      </Row>
      <Row icon="person" label="Assignee" htmlFor={ids.assignee}>
        <select
          id={ids.assignee}
          value={task.assigneeUid === null ? "" : task.assigneeUid}
          onChange={(e) => onSave({ assigneeUid: e.target.value === "" ? null : e.target.value })}
          className={quiet}
        >
          <option value="">Unassigned</option>
          {(members.data === undefined ? [] : members.data).map((m) => (
            <option key={m.uid} value={m.uid}>
              {memberLabel(members.data, m.uid)}
              {m.uid === myUid ? " (me)" : ""}
            </option>
          ))}
          {/* Until the members load, the stored assignee still needs an option to show. */}
          {task.assigneeUid !== null && members.data === undefined ? <option value={task.assigneeUid}>…</option> : null}
        </select>
      </Row>
      <Row icon="calendar" label="Due" htmlFor={ids.due}>
        <input
          id={ids.due}
          type="date"
          value={toDateInput(task.due)}
          onChange={(e) => onSave({ due: fromDateInput(e.target.value) })}
          className={cx(quiet, task.due === null ? "text-muted" : "")}
        />
      </Row>
      <Row icon="lock" label="Visibility" htmlFor={ids.visibility}>
        <select
          id={ids.visibility}
          value={personal ? "private" : task.visibility}
          disabled={personal || !isCreator}
          title={personal ? "Tasks in Personal are always private." : isCreator ? undefined : "Only the person who created the task can change this."}
          onChange={(e) => onSave({ visibility: e.target.value === "private" ? "private" : "workspace" })}
          className={quiet}
        >
          <option value="workspace">Shared with the workspace</option>
          <option value="private">Private (you and the assignee)</option>
        </select>
      </Row>
      {task.source === null ? null : (
        <Row icon="link" label="From">
          {href !== null ? (
            <a href={href.href} target="_blank" rel="noreferrer" className="block truncate px-2 text-sm text-accent hover:underline">
              {SOURCE_LABEL[task.source.kind]} · {href.host}
            </a>
          ) : task.source.kind === "email" ? (
            <Link to="/w/$workspaceId/inbox" params={{ workspaceId }} className="px-2 text-sm text-accent hover:underline">
              Email
            </Link>
          ) : (
            <span className="px-2 text-sm text-text">{SOURCE_LABEL[task.source.kind]}</span>
          )}
        </Row>
      )}
    </dl>
  );
}
